import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  BlockManager,
  BasicType,
  IPage,
} from "@ivanholiak/easy-email-core";
import {
  EmailEditor,
  EmailEditorProvider,
  IEmailTemplate,
} from "@ivanholiak/easy-email-editor";
import { StandardLayout } from "@ivanholiak/easy-email-extensions";
import { getLoggedinUser } from "../../../helpers/api_helper";
import type { Campaign } from "../../../helpers/campaigns_helper";
import {
  CAMPAIGN_ASSET_MAX_BYTES,
  previewCampaignMerge,
  uploadCampaignAsset,
} from "../../../helpers/campaigns_helper";
import { getContacts, type Contact } from "../../../helpers/audience_helper";
import {
  CAMPAIGN_MERGE_TAGS,
  PREVIEW_SAMPLE_TAGS,
  applySampleMergeTags,
  setEasyEmailBrandDefaults,
  applyBrandKitToPage,
  buildCampaignEditorCategories,
  campaignMergeTagGenerate,
  createConditionalWrapperBlock,
  createEmptyCampaignPage,
  ensureEasyEmailBlocks,
  exportEasyEmailHtml,
  findConditionalNodes,
  isEasyEmailPage,
  isPlaceholderCampaignPage,
  updateConditionalAt,
  type BrenvioCondition,
  type BrandKitData,
  type ConditionalNodeInfo,
} from "./easyEmailHelpers";
import { resolveEasyEmailLocale } from "./easyEmailLocale";
import ConditionalBlockModal from "./ConditionalBlockModal";
import TestCampaignModal from "./TestCampaignModal";
import PreflightPanel from "./PreflightPanel";
import {
  checkCampaignDeliverability,
  type DeliverabilityCheckResult,
} from "../../../helpers/deliverability_helper";

// Fork usa prefixCls="ee" — CSS Arco precisa de classes .ee-* (não .arco-*).
// Gerado por: npm run easy-email:css (escopado em .campaign-easy-email).
import "../../../assets/css/easy-email-arco-ee.css";
import "@ivanholiak/easy-email-editor/lib/style.css";
import "@ivanholiak/easy-email-extensions/lib/style.css";
import "./CampaignEasyEmailEditor.scss";

const UPLOAD_TOAST_ID = "campaign-asset-upload";
const WIDE_COMPACT_MQ = "(max-width: 1199.98px)";
const DOCK_PREVIEW_MQ = "(min-width: 1200px)";

type ChangePayload = {
  editor_design: Record<string, unknown>;
  html_body: string;
};

type Props = {
  design: Record<string, unknown> | null;
  subject: string;
  remountKey: string;
  onChange: (payload: ChangePayload) => void;
  /** CTA do empty state — campanhas/templates usam galeria; SA pode omitir. */
  galleryPath?: string | null;
  /**
   * Campanha já salva → botão “Enviar teste” na toolbar.
   * Sem id (rascunho novo): botão avisa para salvar antes.
   */
  testCampaign?: Campaign | null;
  showTestSend?: boolean;
  /** Abre o modal de teste ao montar (ex.: clone da galeria com ?openTest=1). */
  autoOpenTest?: boolean;
  /** Aplica cores/fonte do brand kit no canvas quando a campanha marca use_brand_kit. */
  useBrandKit?: boolean;
};

/** Empurra design+HTML para o form pai fora do render (evita loop). */
const DesignSync = ({
  content,
  onChange,
  onHtmlReady,
}: {
  content: IPage;
  onChange: (payload: ChangePayload) => void;
  onHtmlReady?: (html: string) => void;
}) => {
  const lastHtmlRef = useRef("");

  useEffect(() => {
    try {
      const html = exportEasyEmailHtml(content);
      if (!html || html === lastHtmlRef.current) {
        return;
      }
      lastHtmlRef.current = html;
      onHtmlReady?.(html);
      onChange({
        editor_design: content as unknown as Record<string, unknown>,
        html_body: html,
      });
    } catch {
      // Estrutura incompleta durante drag — ignora.
    }
  }, [content, onChange, onHtmlReady]);

  return null;
};

/**
 * Empty some ao editar o canvas (drag/texto), não só no CTA — LAC-109 item 2.
 * Observa o design vivo do provider; o prop `design` do pai pode atrasar 1 tick.
 */
const EmptyStateWatcher = ({
  content,
  enabled,
  onLeavePlaceholder,
}: {
  content: IPage;
  enabled: boolean;
  onLeavePlaceholder: () => void;
}) => {
  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (!isPlaceholderCampaignPage(content)) {
      onLeavePlaceholder();
    }
  }, [content, enabled, onLeavePlaceholder]);

  return null;
};

function sessionHasTrackingHostname(): boolean {
  const session = getLoggedinUser();
  const host = session?.tenant?.tracking_hostname;
  return typeof host === "string" && host.trim() !== "";
}

function buildLocalPreviewHtml(raw: string): string {
  return applySampleMergeTags(raw);
}

/**
 * Chrome próprio (LAC-107 C): toolbar + empty overlay + StandardLayout envelopado.
 * EditPanel/ConfigurationPanel não são exportados pelo fork — envelopamos StandardLayout
 * com markup/CSS nosso (tema, altura, empty, fullscreen).
 */
const CampaignEasyEmailShell = ({
  height,
  compact,
  categories,
  showEmpty,
  galleryPath,
  onDismissEmpty,
  children,
}: {
  height: string;
  compact: boolean;
  categories: ReturnType<typeof buildCampaignEditorCategories>;
  showEmpty: boolean;
  galleryPath: string | null;
  onDismissEmpty: () => void;
  children: React.ReactNode;
}) => {
  const { t } = useTranslation();

  return (
    <div className="campaign-easy-email__shell">
      <div
        className={
          "campaign-easy-email__canvas-wrap" +
          (compact ? " campaign-easy-email__canvas-wrap--compact" : "")
        }
        style={{ height }}
      >
        {showEmpty ? (
          <div className="campaign-easy-email__empty" role="status">
            <div className="campaign-easy-email__empty-inner">
              <i className="ri-mail-edit-line campaign-easy-email__empty-icon" />
              <h3 className="h5 mb-2">{t("campaigns.form.editor.emptyTitle")}</h3>
              <p className="text-muted mb-3">
                {t("campaigns.form.editor.emptyBody")}
              </p>
              <div className="d-flex flex-wrap gap-2 justify-content-center">
                {galleryPath ? (
                  <Button
                    color="primary"
                    tag={Link}
                    to={galleryPath}
                    className="btn-label"
                  >
                    <i className="ri-gallery-line label-icon align-middle fs-16 me-2" />
                    {t("campaigns.form.editor.emptyGalleryCta")}
                  </Button>
                ) : null}
                <Button color="soft-secondary" onClick={onDismissEmpty}>
                  {t("campaigns.form.editor.emptyStartBlank")}
                </Button>
              </div>
            </div>
          </div>
        ) : null}
        <StandardLayout
          categories={categories}
          showSourceCode={false}
          compact={compact}
        >
          {children}
        </StandardLayout>
      </div>
    </div>
  );
};

/**
 * Easy Email self-hosted (fork React 19) — LAC-107 + LAC-109 + anti-viés polish.
 * Imagens: upload síncrono → URL no host de tracking (LAC-031).
 */
const CampaignEasyEmailEditor = ({
  design,
  subject,
  remountKey,
  onChange,
  galleryPath = "/templates/gallery",
  testCampaign = null,
  showTestSend = false,
  autoOpenTest = false,
  useBrandKit = false,
}: Props) => {
  const { t, i18n } = useTranslation();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const latestPageRef = useRef<IPage | null>(null);
  const wasFullscreenRef = useRef(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocked, setPreviewDocked] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewContactId, setPreviewContactId] = useState<number | null>(null);
  const [previewContactLabel, setPreviewContactLabel] = useState<string | null>(
    null
  );
  const [previewNameEmpty, setPreviewNameEmpty] = useState(false);
  const [previewMode, setPreviewMode] = useState<"contact" | "sample">(
    "sample"
  );
  const [previewSearch, setPreviewSearch] = useState("");
  const [previewHits, setPreviewHits] = useState<Contact[]>([]);
  const [previewSearchBusy, setPreviewSearchBusy] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [preflightOpen, setPreflightOpen] = useState(false);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [preflightResult, setPreflightResult] =
    useState<DeliverabilityCheckResult | null>(null);
  const [preflightError, setPreflightError] = useState<string | null>(null);
  const [compact, setCompact] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia(WIDE_COMPACT_MQ).matches
  );
  const [emptyDismissed, setEmptyDismissed] = useState(false);

  // Clone da galeria com openTest=1 → abre modal assim que a campanha estiver pronta.
  useEffect(() => {
    if (autoOpenTest && showTestSend && testCampaign?.id) {
      setTestOpen(true);
    }
  }, [autoOpenTest, showTestSend, testCampaign?.id]);
  const [conditionalOpen, setConditionalOpen] = useState(false);
  const [conditionalMode, setConditionalMode] = useState<"insert" | "edit">(
    "insert"
  );
  const [conditionalList, setConditionalList] = useState<ConditionalNodeInfo[]>(
    []
  );
  const [conditionalEditIndex, setConditionalEditIndex] = useState(0);
  const [injectKey, setInjectKey] = useState(0);
  const [injectedDesign, setInjectedDesign] = useState<IPage | null>(null);

  ensureEasyEmailBlocks();

  const locale = useMemo(
    () => resolveEasyEmailLocale(i18n.language),
    [i18n.language]
  );

  const categories = useMemo(
    () => buildCampaignEditorCategories(t),
    [t]
  );

  // Brand kit do tenant (da session) — aplicado se useBrandKit=true.
  const brandKit = useMemo<BrandKitData | null>(() => {
    if (!useBrandKit) {
      return null;
    }
    const session = getLoggedinUser();
    const kit = session?.tenant?.brand_kit;
    if (!kit) {
      return null;
    }
    return {
      primary_color: kit.primary_color || null,
      secondary_color: kit.secondary_color || null,
      font_family: kit.font_family || null,
    };
  }, [useBrandKit]);

  useEffect(() => {
    setEasyEmailBrandDefaults(brandKit);
  }, [brandKit]);

  const dismissEmpty = useCallback(() => setEmptyDismissed(true), []);

  const openConditionalInsert = useCallback(() => {
    setConditionalMode("insert");
    setConditionalOpen(true);
  }, []);

  const openConditionalEdit = useCallback(() => {
    const page =
      latestPageRef.current ||
      (isEasyEmailPage(design) ? (design as unknown as IPage) : null);
    if (!page) {
      return;
    }
    const list = findConditionalNodes(page);
    if (list.length === 0) {
      toast.info(t("campaigns.form.editor.conditionalNone"), {
        autoClose: 3000,
      });
      return;
    }
    setConditionalList(list);
    // Se só um condicional: edita direto. Senão: abre select (por ora edita o primeiro).
    setConditionalEditIndex(0);
    setConditionalMode("edit");
    setConditionalOpen(true);
  }, [design, t]);

  const insertConditional = useCallback(
    (condition: BrenvioCondition) => {
      const base =
        latestPageRef.current ||
        (isEasyEmailPage(design)
          ? (design as unknown as IPage)
          : createEmptyCampaignPage());
      const wrapper = createConditionalWrapperBlock(
        condition,
        t("campaigns.form.editor.conditionalPlaceholder")
      );
      const next: IPage = {
        ...base,
        children: [...(Array.isArray(base.children) ? base.children : []), wrapper],
      };
      setInjectedDesign(next);
      setInjectKey((k) => k + 1);
      setEmptyDismissed(true);
      try {
        onChange({
          editor_design: next as unknown as Record<string, unknown>,
          html_body: exportEasyEmailHtml(next),
        });
      } catch {
        onChange({
          editor_design: next as unknown as Record<string, unknown>,
          html_body: "",
        });
      }
    },
    [design, onChange, t]
  );

  const updateConditional = useCallback(
    (condition: BrenvioCondition) => {
      const base =
        latestPageRef.current ||
        (isEasyEmailPage(design)
          ? (design as unknown as IPage)
          : createEmptyCampaignPage());
      if (conditionalList.length === 0) {
        return;
      }
      const target = conditionalList[conditionalEditIndex];
      if (!target) {
        return;
      }
      const next = updateConditionalAt(base, target.path, condition);
      setInjectedDesign(next);
      setInjectKey((k) => k + 1);
      try {
        onChange({
          editor_design: next as unknown as Record<string, unknown>,
          html_body: exportEasyEmailHtml(next),
        });
      } catch {
        onChange({
          editor_design: next as unknown as Record<string, unknown>,
          html_body: "",
        });
      }
    },
    [design, onChange, conditionalList, conditionalEditIndex]
  );

  const refreshPreviewFromPage = useCallback(async () => {
    try {
      const page = latestPageRef.current;
      const raw = page ? exportEasyEmailHtml(page) : "";
      const campaignId = testCampaign?.id;

      // Sem campanha salva: só amostra local (nativos). fields.* via API.
      if (!campaignId) {
        setPreviewHtml(buildLocalPreviewHtml(raw));
        setPreviewMode("sample");
        setPreviewNameEmpty(false);
        return;
      }

      setPreviewLoading(true);
      const res = await previewCampaignMerge(campaignId, {
        contact_id: previewContactId,
        subject,
        html_body: raw,
      });
      setPreviewHtml(res.data.html_body || "");
      setPreviewMode(res.data.mode);
      setPreviewNameEmpty(Boolean(res.data.name_empty));
      if (res.data.contact) {
        const label = res.data.contact.name
          ? `${res.data.contact.name} <${res.data.contact.email}>`
          : res.data.contact.email;
        setPreviewContactLabel(label);
      }
    } catch {
      try {
        const page = latestPageRef.current;
        const raw = page ? exportEasyEmailHtml(page) : "";
        setPreviewHtml(buildLocalPreviewHtml(raw));
      } catch {
        setPreviewHtml("");
      }
    } finally {
      setPreviewLoading(false);
    }
  }, [testCampaign?.id, previewContactId, subject]);

  const onHtmlReady = useCallback(
    (html: string) => {
      // Dock aberto: amostra local ao vivo; contato real exige refresh explícito (API).
      if ((previewDocked || previewOpen) && !previewContactId) {
        setPreviewHtml(buildLocalPreviewHtml(html));
      }
    },
    [previewDocked, previewOpen, previewContactId]
  );

  // Busca leve de contatos para o picker da prévia (LAC-068).
  useEffect(() => {
    const q = previewSearch.trim();
    if (q.length < 2) {
      setPreviewHits([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setPreviewSearchBusy(true);
      try {
        const res = await getContacts({
          search: q,
          status: "active",
          per_page: 8,
        });
        if (!cancelled) {
          setPreviewHits(Array.isArray(res.data) ? res.data : []);
        }
      } catch {
        if (!cancelled) {
          setPreviewHits([]);
        }
      } finally {
        if (!cancelled) {
          setPreviewSearchBusy(false);
        }
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [previewSearch]);

  useEffect(() => {
    if (previewDocked || previewOpen) {
      void refreshPreviewFromPage();
    }
  }, [previewContactId, previewDocked, previewOpen, refreshPreviewFromPage]);

  // Sem tracking hostname a URL cai no fallback Storage — e-mail pode quebrar em prod.
  const hasTrackingHost = useMemo(() => sessionHasTrackingHostname(), [remountKey]);

  const initialValues: IEmailTemplate = useMemo(() => {
    const source = injectedDesign ?? design;
    let content = isEasyEmailPage(source)
      ? (source as unknown as IPage)
      : createEmptyCampaignPage();

    // Aplica brand kit se checkbox marcado e dados disponíveis (F2 polish).
    if (brandKit && (brandKit.primary_color || brandKit.font_family)) {
      content = applyBrandKitToPage(content, brandKit);
    }

    return {
      subject: subject || t("campaigns.form.preview.emptySubject"),
      subTitle: "",
      content,
    };
    // remountKey / injectKey: remonta o provider com design fresco.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remountKey, injectKey, injectedDesign, brandKit, t, subject]);

  // Novo design / remount → empty state de volta se ainda for placeholder.
  useEffect(() => {
    setEmptyDismissed(false);
    setPreviewDocked(false);
    setPreviewOpen(false);
    setInjectedDesign(null);
  }, [remountKey]);

  // Compact só em viewport estreita — tela larga usa painéis lado a lado (LAC-107 A.3).
  useEffect(() => {
    const mq = window.matchMedia(WIDE_COMPACT_MQ);
    const sync = () => setCompact(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  // Esc fecha fullscreen; Ctrl/Cmd+Shift+P abre Preview.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (fullscreen && e.key === "Escape") {
        setFullscreen(false);
        return;
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        e.shiftKey &&
        (e.key === "p" || e.key === "P")
      ) {
        // Evita conflito com “imprimir” do browser (Ctrl+P sem Shift).
        e.preventDefault();
        openPreviewRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen]);

  const openPreview = useCallback(() => {
    void refreshPreviewFromPage();
    const canDock =
      typeof window !== "undefined" &&
      window.matchMedia(DOCK_PREVIEW_MQ).matches;
    if (canDock) {
      // Desktop: painel ao lado do canvas (vê e edita juntos).
      setPreviewOpen(false);
      setPreviewDocked((v) => !v);
      return;
    }
    setPreviewDocked(false);
    setPreviewOpen(true);
  }, [refreshPreviewFromPage]);

  const openPreviewRef = useRef(openPreview);
  openPreviewRef.current = openPreview;

  // Toast ao entrar/sair da tela cheia — LAC-109 + anti-viés.
  useEffect(() => {
    if (!wasFullscreenRef.current && fullscreen) {
      toast.info(t("campaigns.form.editor.fullscreenEnterToast"), {
        autoClose: 2000,
      });
    } else if (wasFullscreenRef.current && !fullscreen) {
      // Draft localStorage já roda no form pai; só lembra de Salvar no servidor.
      toast.info(t("campaigns.form.editor.fullscreenExitToast"), {
        autoClose: 2800,
      });
      setPreviewDocked(false);
    }
    wasFullscreenRef.current = fullscreen;
  }, [fullscreen, t]);

  const showEmpty =
    !emptyDismissed &&
    (design === null || isPlaceholderCampaignPage(design));

  // Altura útil: fullscreen = quase viewport; senão calc do chrome do form (~220px).
  const editorHeight = fullscreen
    ? "calc(100vh - 56px)"
    : "calc(100vh - 220px)";

  const onTestSendClick = () => {
    if (testCampaign?.id) {
      setTestOpen(true);
      return;
    }
    // Campanha nova ainda sem id — API de teste exige registro salvo.
    toast.info(t("campaigns.form.editor.testSaveFirst"), { autoClose: 3500 });
  };

  const runPreflight = useCallback(async () => {
    if (!testCampaign?.id) {
      return;
    }
    setPreflightOpen(true);
    setPreflightLoading(true);
    setPreflightError(null);
    try {
      const page = latestPageRef.current;
      const html = page ? exportEasyEmailHtml(page) : testCampaign.html_body;
      const res = await checkCampaignDeliverability(testCampaign.id, {
        subject: subject || testCampaign.subject,
        html_body: html,
      });
      if (res?.success && res.data) {
        setPreflightResult(res.data);
      } else {
        setPreflightResult(null);
        setPreflightError(t("preflight.retry"));
      }
    } catch (err: unknown) {
      setPreflightResult(null);
      const apiMessage =
        err && typeof err === "object" && "message" in err
          ? String((err as { message?: string }).message || "")
          : "";
      setPreflightError(apiMessage || t("preflight.retry"));
    } finally {
      setPreflightLoading(false);
    }
  }, [testCampaign, subject, t]);

  const onUploadImage = async (blob: Blob): Promise<string> => {
    // Teto client-side = API (5 MiB) — evita round-trip inútil.
    if (blob.size > CAMPAIGN_ASSET_MAX_BYTES) {
      toast.warning(t("campaigns.form.editor.imageTooLarge"), {
        autoClose: 4000,
      });
      throw new Error("image_too_large");
    }

    toast.info(t("campaigns.form.editor.imageUploading"), {
      toastId: UPLOAD_TOAST_ID,
      autoClose: false,
    });

    try {
      const { url } = await uploadCampaignAsset(blob);
      toast.update(UPLOAD_TOAST_ID, {
        render: t("campaigns.form.editor.imageUploadSuccess"),
        type: "success",
        autoClose: 2000,
        isLoading: false,
      });
      return url;
    } catch (err: unknown) {
      toast.dismiss(UPLOAD_TOAST_ID);
      const apiMessage =
        err && typeof err === "object" && "message" in err
          ? String((err as { message?: string }).message || "")
          : "";
      toast.error(apiMessage || t("campaigns.form.editor.imageUploadFailed"), {
        autoClose: 4000,
      });
      throw err instanceof Error ? err : new Error("image_upload_failed");
    }
  };

  const sampleTagList = Object.keys(PREVIEW_SAMPLE_TAGS)
    .filter((k) => k !== "phone" && k !== "name")
    .map((k) => `{{${k}}}`)
    .join(", ");

  const previewContactPicker = (
    <div className="mb-2 px-2">
      {testCampaign?.id ? (
        <>
          <label className="form-label small mb-1" htmlFor="preview-contact-search">
            {t("campaigns.form.editor.previewContactLabel")}
          </label>
          <Input
            id="preview-contact-search"
            bsSize="sm"
            value={previewSearch}
            placeholder={t("campaigns.form.editor.previewContactSearch")}
            onChange={(e) => setPreviewSearch(e.target.value)}
            disabled={previewLoading}
          />
          {previewSearchBusy ? (
            <p className="text-muted small mb-1 mt-1">
              <Spinner size="sm" className="me-1" />
              {t("campaigns.form.editor.previewContactSearching")}
            </p>
          ) : null}
          {previewHits.length > 0 ? (
            <ul className="list-unstyled small mb-1 mt-1 border rounded bg-white">
              {previewHits.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-start w-100 text-decoration-none"
                    onClick={() => {
                      setPreviewContactId(c.id);
                      setPreviewContactLabel(
                        c.name ? `${c.name} <${c.email}>` : c.email
                      );
                      setPreviewSearch("");
                      setPreviewHits([]);
                    }}
                  >
                    {c.name ? `${c.name} — ${c.email}` : c.email}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {previewContactId ? (
            <div className="d-flex align-items-center gap-2 mt-1">
              <span className="small text-truncate">
                {t("campaigns.form.editor.previewUsingContact", {
                  contact: previewContactLabel || `#${previewContactId}`,
                })}
              </span>
              <Button
                color="link"
                size="sm"
                className="p-0 flex-shrink-0"
                type="button"
                onClick={() => {
                  setPreviewContactId(null);
                  setPreviewContactLabel(null);
                  setPreviewNameEmpty(false);
                }}
              >
                {t("campaigns.form.editor.previewUseSample")}
              </Button>
            </div>
          ) : (
            <p className="text-muted small mb-0 mt-1">
              {previewMode === "sample"
                ? t("campaigns.form.editor.previewMergeHint", {
                    tags: sampleTagList,
                  })
                : null}
            </p>
          )}
          {previewNameEmpty ? (
            <Alert color="warning" className="py-1 px-2 mt-2 mb-0 small">
              {t("campaigns.form.editor.previewNameEmpty")}
            </Alert>
          ) : null}
        </>
      ) : (
        <p className="text-muted small mb-0">
          {t("campaigns.form.editor.previewSaveFirst")}
        </p>
      )}
    </div>
  );

  return (
    <div
      ref={rootRef}
      className={
        "campaign-easy-email" +
        (fullscreen ? " campaign-easy-email--fullscreen" : "") +
        (previewDocked ? " campaign-easy-email--preview-docked" : "")
      }
    >
      <div className="campaign-easy-email__toolbar">
        <span className="campaign-easy-email__toolbar-hint text-muted small">
          {t("campaigns.form.editor.toolbarHint")}
        </span>
        <div className="campaign-easy-email__toolbar-actions">
          <Button
            color="soft-warning"
            size="sm"
            type="button"
            className="me-1"
            onClick={openConditionalInsert}
          >
            <i className="ri-git-branch-line align-middle me-1" />
            {t("campaigns.form.editor.conditionalButton")}
          </Button>
          <Button
            color="soft-warning"
            size="sm"
            type="button"
            className="me-1"
            onClick={openConditionalEdit}
            title={t("campaigns.form.editor.conditionalEditHint")}
          >
            <i className="ri-edit-2-line align-middle me-1" />
            {t("campaigns.form.editor.conditionalEdit")}
          </Button>
          <Button
            color="soft-secondary"
            size="sm"
            type="button"
            className="me-1"
            onClick={openPreview}
            aria-pressed={previewDocked || previewOpen}
            title={t("campaigns.form.editor.previewShortcutHint")}
          >
            <i className="ri-eye-line align-middle me-1" />
            {t("campaigns.form.editor.preview")}
          </Button>
          {showTestSend ? (
            <Button
              color="soft-success"
              size="sm"
              type="button"
              className="me-1"
              onClick={onTestSendClick}
            >
              <i className="ri-mail-send-line align-middle me-1" />
              {t("campaigns.form.editor.testSend")}
            </Button>
          ) : null}
          {showTestSend && testCampaign?.id ? (
            <Button
              color="soft-info"
              size="sm"
              type="button"
              className="me-1"
              disabled={preflightLoading}
              onClick={() => void runPreflight()}
            >
              <i className="ri-shield-check-line align-middle me-1" />
              {t("preflight.openButton")}
            </Button>
          ) : null}
          {galleryPath ? (
            <Button
              color="soft-primary"
              size="sm"
              tag={Link}
              to={galleryPath}
              className="me-1"
            >
              <i className="ri-gallery-line align-middle me-1" />
              {t("campaigns.form.editor.toolbarGallery")}
            </Button>
          ) : null}
          <Button
            color="soft-secondary"
            size="sm"
            type="button"
            onClick={() => setFullscreen((v) => !v)}
            aria-pressed={fullscreen}
          >
            <i
              className={
                (fullscreen ? "ri-fullscreen-exit-line" : "ri-fullscreen-line") +
                " align-middle me-1"
              }
            />
            {fullscreen
              ? t("campaigns.form.editor.exitFullscreen")
              : t("campaigns.form.editor.fullscreen")}
          </Button>
        </div>
      </div>

      {!hasTrackingHost ? (
        <Alert color="warning" className="mb-0 rounded-0 py-2 small">
          {t("campaigns.form.editor.trackingNeeded")}{" "}
          <Link to="/settings/sending">
            {t("campaigns.form.editor.trackingCta")}
          </Link>
        </Alert>
      ) : null}

      <div className="campaign-easy-email__workspace">
        <div className="campaign-easy-email__editor-col">
          <EmailEditorProvider
            key={`${remountKey}-${injectKey}-${i18n.language}-${fullscreen ? "fs" : "n"}`}
            data={initialValues}
            height={editorHeight}
            autoComplete
            dashed={false}
            mergeTags={CAMPAIGN_MERGE_TAGS}
            mergeTagGenerate={campaignMergeTagGenerate}
            enabledMergeTagsBadge
            onUploadImage={onUploadImage}
            locale={locale}
          >
            {({ values }) => {
              const page =
                (values?.content as IPage) ||
                BlockManager.getBlockByType(BasicType.PAGE)!.create({});
              latestPageRef.current = page;

              return (
                <>
                  <DesignSync
                    content={page}
                    onChange={onChange}
                    onHtmlReady={onHtmlReady}
                  />
                  <EmptyStateWatcher
                    content={page}
                    enabled={!emptyDismissed}
                    onLeavePlaceholder={dismissEmpty}
                  />
                  <CampaignEasyEmailShell
                    height={editorHeight}
                    compact={compact}
                    categories={categories}
                    showEmpty={showEmpty}
                    galleryPath={galleryPath}
                    onDismissEmpty={dismissEmpty}
                  >
                    <EmailEditor />
                  </CampaignEasyEmailShell>
                </>
              );
            }}
          </EmailEditorProvider>
        </div>

        {previewDocked ? (
          <aside
            className="campaign-easy-email__preview-dock"
            aria-label={t("campaigns.form.editor.previewTitle")}
          >
            <div className="campaign-easy-email__preview-dock-head">
              <strong className="small mb-0">
                {t("campaigns.form.editor.previewTitle")}
              </strong>
              <Button
                color="link"
                size="sm"
                className="p-0"
                type="button"
                onClick={() => setPreviewDocked(false)}
              >
                {t("campaigns.form.editor.previewClose")}
              </Button>
            </div>
            {previewContactPicker}
            {previewLoading ? (
              <p className="text-muted small px-2">
                <Spinner size="sm" className="me-1" />
                {t("campaigns.form.editor.previewLoading")}
              </p>
            ) : null}
            {previewHtml.trim() ? (
              <iframe
                title={t("campaigns.form.editor.previewTitle")}
                sandbox=""
                srcDoc={previewHtml}
                className="campaign-easy-email__preview-frame"
              />
            ) : (
              <p className="text-muted small px-2">
                {t("campaigns.form.editor.previewEmpty")}
              </p>
            )}
          </aside>
        ) : null}
      </div>

      <Modal
        isOpen={previewOpen}
        toggle={() => setPreviewOpen(false)}
        size="lg"
        centered
        zIndex={fullscreen ? 1060 : undefined}
      >
        <ModalHeader toggle={() => setPreviewOpen(false)}>
          {t("campaigns.form.editor.previewTitle")}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted small mb-2">
            {t("campaigns.form.preview.hint")}
          </p>
          {previewContactPicker}
          {previewLoading ? (
            <p className="text-muted small">
              <Spinner size="sm" className="me-1" />
              {t("campaigns.form.editor.previewLoading")}
            </p>
          ) : null}
          {previewHtml.trim() ? (
            <div
              className="border rounded bg-white"
              style={{ minHeight: 320 }}
            >
              <iframe
                title={t("campaigns.form.editor.previewTitle")}
                sandbox=""
                srcDoc={previewHtml}
                style={{ width: "100%", minHeight: 320, border: 0 }}
              />
            </div>
          ) : (
            <p className="text-muted mb-0">
              {t("campaigns.form.editor.previewEmpty")}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setPreviewOpen(false)}>
            {t("campaigns.form.editor.previewClose")}
          </Button>
        </ModalFooter>
      </Modal>

      {showTestSend ? (
        <TestCampaignModal
          campaign={testCampaign}
          isOpen={testOpen}
          onClose={() => setTestOpen(false)}
        />
      ) : null}

      <ConditionalBlockModal
        isOpen={conditionalOpen}
        toggle={() => setConditionalOpen(false)}
        mode={conditionalMode}
        initialCondition={
          conditionalMode === "edit" && conditionalList[conditionalEditIndex]
            ? conditionalList[conditionalEditIndex].condition
            : null
        }
        onInsert={insertConditional}
        onUpdate={updateConditional}
      />

      <Modal
        isOpen={preflightOpen}
        toggle={() => setPreflightOpen(false)}
        size="lg"
        centered
        zIndex={fullscreen ? 1060 : undefined}
      >
        <ModalHeader toggle={() => setPreflightOpen(false)}>
          {t("preflight.title")}
        </ModalHeader>
        <ModalBody>
          <PreflightPanel
            result={preflightResult}
            loading={preflightLoading}
            error={preflightError}
            onRecheck={() => void runPreflight()}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setPreflightOpen(false)}>
            {t("sending.modals.cancel")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default CampaignEasyEmailEditor;
